import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  LayoutList,
  TableProperties,
} from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import ToolExecutionCard from '@/components/admin/ToolExecutionCard';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';

interface ToolExecution {
  id: string;
  tool_name: string;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  status: string;
  duration_ms: number | null;
  created_at: string;
  error_message: string | null;
}

interface PlanStep {
  step: number;
  description: string;
  tool?: string;
  status?: string;
}

interface AgentTask {
  id: string;
  task_type: string;
  status: string;
  trigger_type: string | null;
  trigger_ref: string | null;
  model_used: string | null;
  retry_count: number;
  error: string | null;
  plan: PlanStep[] | null;
  current_step: number | null;
  created_at: string;
  completed_at: string | null;
}

interface TaskDetailResponse {
  task: AgentTask;
  tool_executions: ToolExecution[];
}

const statusBadge: Record<string, { variant: 'green' | 'red' | 'blue' | 'yellow' | 'gray'; label: string }> = {
  pending: { variant: 'yellow', label: 'Pending' },
  planning: { variant: 'blue', label: 'Planning' },
  executing: { variant: 'blue', label: 'Executing' },
  waiting: { variant: 'yellow', label: 'Waiting' },
  completed: { variant: 'green', label: 'Completed' },
  failed: { variant: 'red', label: 'Failed' },
  escalated: { variant: 'red', label: 'Escalated' },
};

const stepStatusIcon: Record<string, React.ReactNode> = {
  completed: <CheckCircle2 className="w-4 h-4 text-green-600" />,
  failed: <XCircle className="w-4 h-4 text-red-500" />,
  running: <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />,
  pending: <Clock className="w-4 h-4 text-gray-400" />,
};

function ExpandableJson({ obj, maxLen = 120 }: { obj: Record<string, unknown> | null; maxLen?: number }) {
  if (!obj) return <span className="text-xs font-mono text-gray-400">—</span>;
  const full = JSON.stringify(obj, null, 2);
  const preview = JSON.stringify(obj);
  if (preview.length <= maxLen) {
    return <span className="text-xs font-mono text-gray-500">{preview}</span>;
  }
  return (
    <details className="group">
      <summary className="text-xs font-mono text-gray-500 cursor-pointer hover:text-gray-700 list-none flex items-center gap-1">
        <span className="text-gray-400 group-open:rotate-90 transition-transform inline-block">▶</span>
        <span className="truncate">{preview.slice(0, maxLen)}…</span>
      </summary>
      <pre className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs font-mono text-gray-700 overflow-x-auto whitespace-pre">{full}</pre>
    </details>
  );
}

export default function AgentTaskDetail() {
  const { id } = useParams<{ id: string }>();
  const addToast = useUIStore((s) => s.addToast);
  const [task, setTask] = useState<AgentTask | null>(null);
  const [executions, setExecutions] = useState<ToolExecution[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [viewMode, setViewMode] = useState<'timeline' | 'table'>('timeline');

  const ACTIVE_STATUSES = ['pending', 'planning', 'executing', 'waiting'];

  const load = async (silent = false) => {
    if (!id) return;
    try {
      if (!silent) setIsLoading(true);
      const res = await api.get<TaskDetailResponse>(`/admin/agent/tasks/${id}`);
      setTask(res.task);
      setExecutions(res.tool_executions);
    } catch (err) {
      if (!silent) {
        addToast(err instanceof Error ? err.message : 'Failed to load task', 'error');
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  // Poll every 5s while task is in an active state
  useEffect(() => {
    if (!task || !ACTIVE_STATUSES.includes(task.status)) return;
    const interval = setInterval(() => load(true), 5_000);
    return () => clearInterval(interval);
  }, [id, task?.status]);

  const handleRetry = async () => {
    if (!id) return;
    try {
      setRetrying(true);
      await api.post(`/admin/agent/tasks/${id}/retry`);
      addToast('Task queued for retry', 'success');
      await load();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to retry task', 'error');
    } finally {
      setRetrying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Link to="/admin/agent/tasks" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
          <ChevronLeft className="w-4 h-4" />
          Back to Tasks
        </Link>
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="space-y-4">
        <Link to="/admin/agent/tasks" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
          <ChevronLeft className="w-4 h-4" />
          Back to Tasks
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">Task not found.</div>
      </div>
    );
  }

  const badge = statusBadge[task.status] ?? { variant: 'gray' as const, label: task.status };
  const canRetry = task.status === 'failed' || task.status === 'escalated';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to="/admin/agent/tasks"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mb-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Tasks
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 font-mono">{task.task_type}</h1>
          <p className="text-gray-500 text-sm mt-1">Task ID: {task.id}</p>
        </div>
        {canRetry && (
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {retrying ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Retry Task
          </button>
        )}
      </div>

      {/* Error Banner */}
      {task.error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800 text-sm">Task Error</p>
            <p className="text-red-700 text-sm mt-1">{task.error}</p>
          </div>
        </div>
      )}

      {/* Task Metadata */}
      <Card title="Task Details" padding="md">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          <MetaField label="Status">
            <Badge variant={badge.variant}>{badge.label}</Badge>
          </MetaField>
          <MetaField label="Trigger">
            <span className="text-sm text-gray-700">{task.trigger_type ?? '—'}</span>
          </MetaField>
          <MetaField label="Model Used">
            <span className="text-sm font-mono text-gray-700">{task.model_used ?? '—'}</span>
          </MetaField>
          <MetaField label="Retries">
            <span className="text-sm text-gray-700">{task.retry_count}</span>
          </MetaField>
          <MetaField label="Created">
            <span className="text-sm text-gray-700">{new Date(task.created_at).toLocaleString()}</span>
          </MetaField>
          <MetaField label="Completed">
            <span className="text-sm text-gray-700">
              {task.completed_at ? new Date(task.completed_at).toLocaleString() : '—'}
            </span>
          </MetaField>
        </div>
      </Card>

      {/* Execution Plan */}
      {task.plan && task.plan.length > 0 && (
        <Card title="Execution Plan" padding="md">
          <ol className="space-y-3">
            {task.plan.map((step, i) => {
              const icon = stepStatusIcon[step.status ?? 'pending'] ?? stepStatusIcon['pending'];
              const isActive =
                ACTIVE_STATUSES.includes(task.status) && task.current_step != null && task.current_step === i;
              return (
                <li
                  key={i}
                  aria-current={isActive ? 'step' : undefined}
                  className={cn(
                    'flex items-start gap-3 rounded-lg px-2 py-1 -mx-2 transition-colors',
                    isActive && 'bg-blue-50 ring-1 ring-blue-200'
                  )}
                >
                  <div className={cn(
                    'flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold shrink-0',
                    isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                  )}>
                    {step.step ?? i + 1}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-sm text-gray-800 flex items-center gap-2">
                      {step.description}
                      {isActive && (
                        <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse" aria-hidden />
                      )}
                    </p>
                    {step.tool && (
                      <p className="text-xs font-mono text-gray-400 mt-0.5">{step.tool}</p>
                    )}
                  </div>
                  <div className="pt-1.5 shrink-0">{icon}</div>
                </li>
              );
            })}
          </ol>
        </Card>
      )}

      {/* Tool Executions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Tool Executions</h2>
          {executions.length > 0 && (
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('timeline')}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                  viewMode === 'timeline' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                )}
              >
                <LayoutList className="w-3.5 h-3.5" />
                Timeline
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                  viewMode === 'table' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                )}
              >
                <TableProperties className="w-3.5 h-3.5" />
                Table
              </button>
            </div>
          )}
        </div>

        {executions.length === 0 ? (
          <Card padding="md">
            <div className="p-4 text-center text-gray-500">No tool executions recorded.</div>
          </Card>
        ) : viewMode === 'timeline' ? (
          <div className="space-y-3">
            {executions.map((exec) => (
              <ToolExecutionCard key={exec.id} execution={exec} />
            ))}
          </div>
        ) : (
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tool</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Input</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Output</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {executions.map((exec) => {
                    const b = statusBadge[exec.status] ?? { variant: 'gray' as const, label: exec.status };
                    return (
                      <tr key={exec.id} className="hover:bg-gray-50">
                        <td className="px-6 py-3 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-800">{exec.tool_name}</span>
                        </td>
                        <td className="px-6 py-3 max-w-[280px]">
                          <ExpandableJson obj={exec.input} />
                        </td>
                        <td className="px-6 py-3 max-w-[280px]">
                          {exec.error_message ? (
                            <span className="text-xs text-red-600 break-words block">{exec.error_message}</span>
                          ) : (
                            <ExpandableJson obj={exec.output} />
                          )}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <Badge variant={b.variant}>{b.label}</Badge>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                          {exec.duration_ms != null ? `${exec.duration_ms}ms` : '—'}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                          {new Date(exec.created_at).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function MetaField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      {children}
    </div>
  );
}
